import { Form } from '@epages/react-components'
import type { ReactElement } from 'react'
import { useRef } from 'react'
import Immutable from 'immutable'

import { extractVimeoId, extractYoutubeId } from './extractVideoId'
import { useTranslate } from '../../../../../utils/translate'
import Button from '../../../../formComponents/Button'
import ButtonRow from '../../../../formComponents/ButtonRow'
import FormGroup from '../../../../formComponents/FormGroup'
import InputField from '../../../../formComponents/InputField'

function normalize(data: ImmutableMap): ImmutableMap {
  return data.update('src', (src) => (src ? src.trim() : ''))
}

function getVideoId(url: string) {
  return extractYoutubeId(url) || extractVimeoId(url)
}

export default function VideoSettings({
  data,
  onDataChange,
  onCancel,
  onSave,
  renderLayout,
}: WorkspacePluginSettingsProps): ReactElement {
  const t = useTranslate('interface', 'components.videoElementComponent.settingsModal')

  const validate = (data: ImmutableMap, isOnSubmit = true): ImmutableMap | null => {
    return !isOnSubmit
      ? null
      : Immutable.fromJS({
          src:
            data.get('src') && !getVideoId(data.get('src')) ? t('urlInputField.validationMessages.invalidURL') : null,
        })
  }

  const videoId = useRef(getVideoId(data.get('src')))
  const onChange = (data: ImmutableMap) => {
    const id = getVideoId(data.get('src'))

    // Trigger editor preview if the video ID changed
    if (id && id !== videoId.current) {
      videoId.current = id
      onDataChange(normalize(data))
    }
  }

  const title = data.get('src') ? t('title.onEdit') : t('title.onCreate')

  const body = (
    <FormGroup name="src" tagName="label" label={t('urlInputField.label')}>
      <InputField autoFocus name="src" type="text" />
    </FormGroup>
  )

  const footer = (
    <ButtonRow>
      <Button variant="cancel" type="button" onClick={onCancel}>
        {t('cancelButton.label')}
      </Button>
      <Button>{t('saveButton.label')}</Button>
    </ButtonRow>
  )

  return (
    <Form
      name="pluginVideoSettings"
      value={data}
      onChange={onChange}
      onSubmit={onSave}
      validate={validate}
      normalize={normalize}
    >
      {renderLayout({ title, body, footer })}
    </Form>
  )
}
